/**
 * BillingDashboard - Dashboard de Facturación
 * Muestra los últimos documentos emitidos con stats y acciones
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
} from '@mui/material';
import {
  Receipt,
  Description,
  Cancel,
  Visibility,
  PictureAsPdf,
  Email,
} from '@mui/icons-material';
import { dteService, invoiceService, resolvePdfUrl } from '../../services/api';
import { getDocumentAmount, getDocumentAmountLabel } from './utils/documentAmounts';

const DTE_TYPES = {
  '01': 'Factura',
  '03': 'Crédito Fiscal',
  '04': 'Nota de Remisión',
  '05': 'Nota de Crédito',
  '06': 'Nota de Débito',
  '07': 'Retención',
  '08': 'Liquidación',
  '09': 'DCL',
  '11': 'Exportación',
  '14': 'Sujeto Excluido',
  '15': 'Donación'
};

const STATUS_COLORS = {
  'draft': 'default',
  'signed': 'info',
  'sent': 'warning',
  'accepted': 'success',
  'rejected': 'error',
  'processing': 'warning',
  'cancelled': 'error',
  // Estados del Ministerio de Hacienda
  'PROCESADO': 'success',
  'RECHAZADO': 'error',
  'RECIBIDO CON OBSERVACIONES': 'warning',
  'ENVIADO': 'info',
  'PENDIENTE': 'warning',
};

const STATUS_LABELS = {
  'draft': 'Borrador',
  'signed': 'Firmado',
  'sent': 'Enviado',
  'accepted': 'Aceptado',
  'rejected': 'Rechazado',
  'processing': 'Procesando',
  'cancelled': 'Anulado',
  // Estados del Ministerio de Hacienda
  'PROCESADO': 'Procesado',
  'RECHAZADO': 'Rechazado',
  'RECIBIDO CON OBSERVACIONES': 'Recibido con Obs.',
  'ENVIADO': 'Enviado',
  'PENDIENTE': 'Pendiente',
};

// Tipos de anulación según el esquema
const ANULATION_TYPES = [
  { code: 1, name: 'Anulación por error en el documento', requiresReplacement: true },
  { code: 2, name: 'Anulación por documento incompleto', requiresReplacement: false },
  { code: 3, name: 'Anulación por otros motivos', requiresReplacement: true, requiresDescription: true },
];

// Tipos de documento de identificación (CAT-22)
const ID_TYPES = [
  { code: '36', name: 'NIT' },
  { code: '13', name: 'DUI' },
  { code: '02', name: 'Carnet de Residente' },
  { code: '03', name: 'Pasaporte' },
  { code: '37', name: 'Otro' },
];

const BillingDashboard = () => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedRow, setExpandedRow] = useState(null);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalDocuments, setTotalDocuments] = useState(0);
  const [fechaEmisionFilter, setFechaEmisionFilter] = useState('');
  const [tipoDteFilter, setTipoDteFilter] = useState('');
  
  // Estados para el diálogo de anulación
  const [anulacionDialogOpen, setAnulacionDialogOpen] = useState(false);
  const [anulacionLoading, setAnulacionLoading] = useState(false);
  const [anulacionError, setAnulacionError] = useState('');
  const [anulacionSuccess, setAnulacionSuccess] = useState(false);
  const [emisorData, setEmisorData] = useState(null);
  
  // Estados para el diálogo de PDF
  const [pdfDialogOpen, setPdfDialogOpen] = useState(false);
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [pdfError, setPdfError] = useState('');
  const [invoiceData, setInvoiceData] = useState(null);
  const [loadingInvoiceData, setLoadingInvoiceData] = useState(false);
  
  // Estados para el diálogo de envío de correo
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [emailLoading, setEmailLoading] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [emailSuccess, setEmailSuccess] = useState(false);
  const [emailTo, setEmailTo] = useState('');
  
  // Datos del formulario de anulación
  const [anulacionData, setAnulacionData] = useState({
    tipoAnulacion: null,
    motivoAnulacion: '',
    nombreResponsable: '',
    tipDocResponsable: '',
    numDocResponsable: '',
    nombreSolicita: '',
    tipDocSolicita: '',
    numDocSolicita: '',
    codigoGeneracionR: '', // Código de reemplazo si aplica
  });
  
  // Stats
  const [stats, setStats] = useState({
    totalDocumentos: 0,
    emitidos: 0,
    anulados: 0,
    totalMonto: 0
  });
  
  // Obtener companyId (ya no se usa para llamadas API, se obtiene del token)
  const getCompanyId = () => {
    try {
      // Intentar obtener de nx2026_modules primero
      const modules = localStorage.getItem('nx2026_modules');
      if (modules) {
        const parsed = JSON.parse(modules);
        // Buscar el módulo de facturación para obtener la empresa
        const billingModule = parsed.find(m => 
          m.modules && m.modules.some(mod => mod.id && mod.id.toUpperCase().includes('BILLING'))
        );
        if (billingModule && billingModule.company) {
          return billingModule.company.id;
        }
        // Si no hay módulo de facturación, usar la primera empresa
        if (parsed[0] && parsed[0].company) {
          return parsed[0].company.id;
        }
      }
      
      // Intentar obtener de nx2026_company
      const company = localStorage.getItem('nx2026_company');
      if (company) {
        const parsed = JSON.parse(company);
        return parsed.id;
      }
    } catch (e) {
      console.error('Error getting companyId:', e);
    }
    return null;
  };
  
  // Función para obtener el estado desde response_payload (campo 'estado' del JSON del Ministerio de Hacienda)
  const getMhEstado = (doc) => {
    // Primero intentar con response_payload
    if (doc.response_payload) {
      try {
        const payload = typeof doc.response_payload === 'string' ? JSON.parse(doc.response_payload) : doc.response_payload;
        if (payload && payload.estado) {
          return payload.estado;
        }
      } catch (e) {
        console.log('[Dashboard] getMhEstado - error parsing response_payload:', e);
      }
    }
    
    // Fallback: intentar con mh_estado
    if (doc.mh_estado) {
      return doc.mh_estado;
    }
    
    return null;
  };
  
  // Cargar documentos recientes
  const loadRecentDocuments = async () => {
    setLoading(true);
    setError('');
    
    try {
      const response = await dteService.getRecent({
        page: page + 1,
        pageSize: rowsPerPage,
        fechaEmision: fechaEmisionFilter,
        tipoDte: tipoDteFilter,
      });

      const pageDocuments = Array.isArray(response?.data) ? response.data : [];
      const pagination = response?.pagination || {};
      const responseStats = response?.stats || {};

      setDocuments(pageDocuments);
      setTotalDocuments(Number(pagination.total || 0));
      setStats({
        totalDocumentos: Number(responseStats.totalDocumentos || 0),
        emitidos: Number(responseStats.emitidos || 0),
        anulados: Number(responseStats.anulados || 0),
        totalMonto: Number(responseStats.totalMonto || 0),
      });
    } catch (err) {
      console.error('[Dashboard] Error:', err);
      setError(err.response?.data?.error || err.message || 'Error al cargar documentos');
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    loadRecentDocuments();
  }, [page, rowsPerPage, fechaEmisionFilter, tipoDteFilter]);
  
  // Función para obtener el color del estado
  const getStatusColor = (status) => {
    if (!status) return 'default';
    return STATUS_COLORS[status] || 'default';
  };
  
  // Función para obtener la etiqueta del estado
  const getStatusLabel = (status) => {
    if (!status) return 'Desconocido';
    return STATUS_LABELS[status] || status;
  };
  
  // Cambiar página
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };
  
  // Cambiar filas por página
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Toggle row expandido
  const handleToggleExpand = (docId) => {
    setExpandedRow(expandedRow === docId ? null : docId);
  };
  
  // Ver detalles del documento
  const handleViewDetails = (doc) => {
    setSelectedDocument(doc);
    setDetailsDialogOpen(true);
  };
  
  // Anular documento - abrir diálogo
  const handleAnular = (doc) => {
    // Solo permitir anulación de documentos procesados
    const mhEstado = getMhEstado(doc);
    if (mhEstado !== 'PROCESADO' && doc.display_status !== 'PROCESADO') {
      setAnulacionError('Solo se pueden anular documentos con estado PROCESADO');
      return;
    }
    
    // Por ahora, solo abrimos el diálogo (la validación de permisos se hace en el backend)
    setSelectedDocument(doc);
    setAnulacionData({
      tipoAnulacion: null,
      motivoAnulacion: '',
      nombreResponsable: '',
      tipDocResponsable: '',
      numDocResponsable: '',
      nombreSolicita: '',
      tipDocSolicita: '',
      numDocSolicita: '',
      codigoGeneracionR: '',
    });
    setAnulacionError('');
    setAnulacionSuccess(false);
    setAnulacionDialogOpen(true);
    
    // Cargar datos del emisor - pasar el documento directamente
    loadEmisorData(doc);
  };
  
  // Función para decodificar JWT (sin verificación de firma)
  const decodeJWT = (token) => {
    try {
      const parts = token.split('.');
      if (parts.length !== 3) {
        console.log('[Dashboard] decodeJWT - token no tiene 3 partes');
        return null;
      }
      
      // Decodificar el payload (parte central)
      const payload = parts[1];
      // Añadir padding si es necesario
      const paddedPayload = payload + '='.repeat((4 - payload.length % 4) % 4);
      const decoded = atob(paddedPayload);
      return JSON.parse(decoded);
    } catch (err) {
      console.error('[Dashboard] decodeJWT - error:', err);
      return null;
    }
  };
  
  // Cargar datos del documento para anulación
  const loadEmisorData = (doc) => {
    if (!doc) {
      console.log('[Dashboard] loadEmisorData - doc es null');
      return;
    }
    
    console.log('[Dashboard] loadEmisorData - iniciando para:', doc.cod_generacion);
    console.log('[Dashboard] loadEmisorData - doc tiene request_payload:', !!doc.request_payload);
    
    // Primer intento: extraer emisor desde request_payload (JWT) del documento
    // El documento puede tener request_payload directamente o necesitamos buscar en la lista
    if (doc.request_payload) {
      try {
        const decodedJWT = decodeJWT(doc.request_payload);
        console.log('[Dashboard] loadEmisorData - JWT decodificado, keys:', decodedJWT ? Object.keys(decodedJWT) : null);
        
        if (decodedJWT && decodedJWT.emisor) {
          const emisor = decodedJWT.emisor;
          console.log('[Dashboard] emisor desde JWT:', emisor);
          
          const identificacion = decodedJWT.identificacion || {};
          
          setEmisorData({
            nit: emisor.nit || '',
            nombre: emisor.nombre || '',
            legal_name: emisor.nombre || emisor.nombreComercial || '',
            commercial_name: emisor.nombreComercial || emisor.nombre || '',
            bill_email: emisor.correo || '',
            bill_phone: emisor.telefono || '',
            correo: emisor.correo || '',
            telefono: emisor.telefono || '',
            environment: identificacion.ambiente === '01' ? 'production' : 'test',
            tipoEstablecimiento: emisor.tipoEstablecimiento || '01',
            codEstable: emisor.codEstable || '0001',
            codPuntoVenta: emisor.codPuntoVenta || '0001',
            nomEstablecimiento: emisor.nomEstablecimiento || emisor.nombreComercial || emisor.nombre || 'Establecimiento Principal'
          });
          console.log('[Dashboard] emisorData configurado desde JWT');
          return;
        }
      } catch (err) {
        console.log('[Dashboard] loadEmisorData - error extrayendo desde JWT:', err);
      }
    }
    
    // Segundo intento: buscar el documento en la lista de documentos para obtener el JWT
    console.log('[Dashboard] loadEmisorData - buscando en documents array...');
    const docFromList = documents.find(d => d.cod_generacion === doc.cod_generacion);
    if (docFromList && docFromList.request_payload) {
      console.log('[Dashboard] loadEmisorData - encontrado en lista');
      try {
        const decodedJWT = decodeJWT(docFromList.request_payload);
        if (decodedJWT && decodedJWT.emisor) {
          const emisor = decodedJWT.emisor;
          const identificacion = decodedJWT.identificacion || {};
          
          setEmisorData({
            nit: emisor.nit || '',
            nombre: emisor.nombre || '',
            legal_name: emisor.nombre || emisor.nombreComercial || '',
            commercial_name: emisor.nombreComercial || emisor.nombre || '',
            bill_email: emisor.correo || '',
            bill_phone: emisor.telefono || '',
            correo: emisor.correo || '',
            telefono: emisor.telefono || '',
            environment: identificacion.ambiente === '01' ? 'production' : 'test',
            tipoEstablecimiento: emisor.tipoEstablecimiento || '01',
            codEstable: emisor.codEstable || '0001',
            codPuntoVenta: emisor.codPuntoVenta || '0001',
            nomEstablecimiento: emisor.nomEstablecimiento || emisor.nombreComercial || emisor.nombre || 'Establecimiento Principal'
          });
          console.log('[Dashboard] emisorData configurado desde lista');
          return;
        }
      } catch (err) {
        console.log('[Dashboard] loadEmisorData - error decodificando JWT de lista:', err);
      }
    }
    
    // Tercer intento: llamar a la API
    console.log('[Dashboard] loadEmisorData - intentando con API...');
    dteService.getDocument(doc.cod_generacion)
      .then(response => {
        console.log('[Dashboard] loadEmisorData - respuesta:', response);
        
        if (response?.success && response.emisor) {
          const emisor = response.emisor;
          console.log('[Dashboard] emisor desde API:', emisor);
          setEmisorData({
            nit: emisor.nit || '',
            nombre: emisor.nombre || '',
            legal_name: emisor.nombre || emisor.nombreComercial || '',
            commercial_name: emisor.nombreComercial || emisor.nombre || '',
            bill_email: emisor.correo || '',
            bill_phone: emisor.telefono || '',
            correo: emisor.correo || '',
            telefono: emisor.telefono || '',
            environment: 'production',
            tipoEstablecimiento: emisor.tipoEstablecimiento || '01',
            codEstable: emisor.codEstable || '0001',
            codPuntoVenta: emisor.codPuntoVenta || '0001',
            nomEstablecimiento: emisor.nombreComercial || emisor.nombre || 'Establecimiento Principal'
          });
        } else {
          console.log('[Dashboard] loadEmisorData - response sin emisor, usando por defecto');
          // Usar valores por defecto
          setEmisorData({
            nit: '',
            legal_name: 'Empresa',
            commercial_name: 'Empresa',
            bill_email: '',
            bill_phone: '',
            environment: 'test',
            tipoEstablecimiento: '01',
            codEstable: '0001',
            codPuntoVenta: '0001',
            nomEstablecimiento: 'Establecimiento Principal'
          });
        }
      })
      .catch(err => {
        console.error('Error loading emisor data:', err);
        setEmisorData({
          nit: '',
          legal_name: 'Empresa',
          commercial_name: 'Empresa',
          bill_email: '',
          bill_phone: '',
          environment: 'test',
          tipoEstablecimiento: '01',
          codEstable: '0001',
          codPuntoVenta: '0001',
          nomEstablecimiento: 'Establecimiento Principal'
        });
      });
  };
  
  // Cerrar diálogo de detalles
  const handleCloseDetailsDialog = () => {
    setDetailsDialogOpen(false);
    setSelectedDocument(null);
  };
  
  // Cerrar diálogo de anulación
  const handleCloseAnulacionDialog = () => {
    setAnulacionDialogOpen(false);
    setSelectedDocument(null);
    setAnulacionError('');
    setAnulacionSuccess(false);
  };
  
  // Generar PDF del documento
  const handleGeneratePdf = async () => {
    if (!selectedDocument) return;
    
    setGeneratingPdf(true);
    setPdfError('');
    
    try {
      const targetId =
        selectedDocument.doc_id ||
        selectedDocument.factura_id ||
        selectedDocument.cod_generacion ||
        selectedDocument.codigo_generacion ||
        selectedDocument.id;

      if (!targetId) {
        setPdfError('No se pudo resolver el identificador del documento para generar el PDF');
        return;
      }

      console.log('[PDF] Generando PDF para identificador:', targetId);

      const result = await invoiceService.generatePdf(targetId);
      
      if (result.success && result.pdfUrl) {
        setPdfUrl(result.pdfUrl);
      } else {
        setPdfError(result.error || 'Error al generar PDF');
      }
    } catch (err) {
      console.error('Error generando PDF:', err);
      setPdfError(err.response?.data?.error || err.message || 'Error al generar PDF');
    } finally {
      setGeneratingPdf(false);
    }
  };
  
  // Abrir diálogo de PDF - usar datos directamente del documento
  const handleOpenPdfDialog = (doc) => {
    console.log('[PDF] Documento recibido:', doc);
    
    setSelectedDocument(doc);
    setPdfUrl(null);
    setPdfError('');
    setInvoiceData(null);
    setLoadingInvoiceData(false);
    setPdfDialogOpen(true);
    
    // No necesitamos llamar a ninguna API para obtener datos
    // Los datos ya están en el objeto doc
  };
  
  // Cerrar diálogo de PDF
  const handleClosePdfDialog = () => {
    setPdfDialogOpen(false);
    setPdfUrl(null);
    setPdfError('');
    setSelectedDocument(null);
    setInvoiceData(null);
    setLoadingInvoiceData(false);
  };
  
  // Abrir diálogo de envío de correo
  const handleOpenEmailDialog = (doc) => {
    setSelectedDocument(doc);
    setEmailTo('');
    setEmailError('');
    setEmailSuccess(false);
    setEmailDialogOpen(true);
  };
  
  // Cerrar diálogo de correo
  const handleCloseEmailDialog = () => {
    setEmailDialogOpen(false);
    setSelectedDocument(null);
    setEmailTo('');
    setEmailError('');
    setEmailSuccess(false);
  };
  
  // Enviar correo con PDF
  const handleSendEmail = async () => {
    if (!emailTo || !emailTo.includes('@')) {
      setEmailError('Por favor ingrese un correo electrónico válido');
      return;
    }
    
    setEmailLoading(true);
    setEmailError('');
    
    try {
      const documentId = selectedDocument.cod_generacion || selectedDocument.id;
      const result = await dteService.sendEmail(documentId, emailTo, selectedDocument.receptor_name);
      
      if (result.success) {
        setEmailSuccess(true);
      } else {
        setEmailError(result.message || 'Error al enviar correo');
      }
    } catch (err) {
      console.error('Error sending email:', err);
      setEmailError(err.response?.data?.error || err.message || 'Error al enviar correo');
    } finally {
      setEmailLoading(false);
    }
  };
  
  // Manejar cambio en campos del formulario de anulación
  const handleAnulacionFieldChange = (field) => (event) => {
    const value = event.target.value;
    setAnulacionData(prev => ({
      ...prev,
      [field]: value
    }));
  };
  
  // Validar formulario de anulación
  const validateAnulacionForm = () => {
    const errors = [];
    
    if (!anulacionData.tipoAnulacion) {
      errors.push('Tipo de anulación es requerido');
    }
    
    if (anulacionData.tipoAnulacion === 3 && !anulacionData.motivoAnulacion) {
      errors.push('Motivo de anulación es requerido para el tipo seleccionado');
    }
    
    if (!anulacionData.nombreResponsable) {
      errors.push('Nombre del responsable es requerido');
    }
    
    if (!anulacionData.tipDocResponsable) {
      errors.push('Tipo de documento del responsable es requerido');
    }
    
    if (!anulacionData.numDocResponsable) {
      errors.push('Número de documento del responsable es requerido');
    }
    
    if (!anulacionData.nombreSolicita) {
      errors.push('Nombre del solicitante es requerido');
    }
    
    if (!anulacionData.tipDocSolicita) {
      errors.push('Tipo de documento del solicitante es requerido');
    }
    
    if (!anulacionData.numDocSolicita) {
      errors.push('Número de documento del solicitante es requerido');
    }
    
    if (anulacionData.tipoAnulacion === 1 && !anulacionData.codigoGeneracionR) {
      errors.push('Código de generación de reemplazo es requerido para este tipo de anulación');
    }
    
    return errors;
  };
  
  // Enviar anulación al backend
  const handleSubmitAnulacion = async () => {
    const errors = validateAnulacionForm();
    if (errors.length > 0) {
      setAnulacionError(errors.join('\n'));
      return;
    }
    
    setAnulacionLoading(true);
    setAnulacionError('');
    
    try {
      const receptorTipoDocumento =
        selectedDocument?.tipoDocumento ||
        selectedDocument?.receptor_tipoDocumento ||
        (selectedDocument?.receptor_nit ? '36' : '13');

      const receptorNumDocumento =
        selectedDocument?.numDocumento ||
        selectedDocument?.receptor_numDocumento ||
        selectedDocument?.receptor_nit ||
        selectedDocument?.receptor_nrc ||
        '';

      // Obtener fecha y hora actual
      const now = new Date();
      const fecAnula = now.toISOString().split('T')[0];
      const horAnula = now.toTimeString().split(' ')[0];
      
      // Construir el payload según el esquema de anulación
      const payload = {
        anulacionJson: {
          identificacion: {
            version: 2,
            ambiente: emisorData?.environment === 'production' ? '01' : '00',
            codigoGeneracion: crypto.randomUUID(), // Nuevo código para la anulación
            fecAnula,
            horAnula,
          },
          emisor: {
            nit: emisorData?.nit || '',
            nombre: emisorData?.legal_name || emisorData?.commercial_name || emisorData?.nombre || '',
            tipoEstablecimiento: emisorData?.tipoEstablecimiento || '01',
            telefono: emisorData?.bill_phone || emisorData?.telefono || '',
            correo: emisorData?.bill_email || emisorData?.correo || '',
            codEstable: emisorData?.codEstable || '0001',
            codPuntoVenta: emisorData?.codPuntoVenta || '0001',
            nomEstablecimiento: emisorData?.nomEstablecimiento || emisorData?.commercial_name || 'Establecimiento Principal',
          },
          documento: {
            tipoDte: selectedDocument.tipo_dte,
            codigoGeneracion: selectedDocument.cod_generacion,
            selloRecibido: selectedDocument.mh_sello_recepcion || '',
            numeroControl: selectedDocument.numero_control || '',
            fecEmi: selectedDocument.fecha_emision || selectedDocument.fecha_formatted,
            montoIva: parseFloat(selectedDocument.monto_total || 0) * 0.13, // Calcular IVA aproximado
            codigoGeneracionR: anulacionData.tipoAnulacion === 2 ? null : anulacionData.codigoGeneracionR || null,
            tipoDocumento: receptorTipoDocumento,
            numDocumento: receptorNumDocumento,
            nombre: selectedDocument.receptor_name || 'Consumidor Final',
          },
          motivo: {
            tipoAnulacion: parseInt(anulacionData.tipoAnulacion),
            motivoAnulacion: anulacionData.tipoAnulacion === 3 ? anulacionData.motivoAnulacion : null,
            nombreResponsable: anulacionData.nombreResponsable,
            tipDocResponsable: anulacionData.tipDocResponsable,
            numDocResponsable: anulacionData.numDocResponsable,
            nombreSolicita: anulacionData.nombreSolicita,
            tipDocSolicita: anulacionData.tipDocSolicita,
            numDocSolicita: anulacionData.numDocSolicita,
          },
        },
        codigoGeneracion: selectedDocument.cod_generacion
      };
      
      console.log('[Anulacion] Enviando payload:', JSON.stringify(payload, null, 2));
      
      // Enviar al backend
      const response = await dteService.anular(payload);
      
      console.log('[Anulacion] Response:', response);
      
      // El backend retorna 'estado' en español, no 'success' en inglés
      // Verificar si la anulación fue exitosa usando el campo 'estado'
      if (response.estado === 'PROCESADO' || response.codigoMsg === '001') {
        setAnulacionSuccess(true);
        
        // Recargar documentos después de la anulación
        setTimeout(() => {
          loadRecentDocuments();
        }, 1500);
      } else {
        // Mostrar el mensaje de error del backend si existe
        const errorMsg = response.descripcionMsg || response.message || response.error || 'Error al anular documento';
        setAnulacionError(errorMsg);
      }
      
    } catch (err) {
      console.error('[Anulacion] Error:', err);
      setAnulacionError(err.response?.data?.error || err.message || 'Error al anular documento');
    } finally {
      setAnulacionLoading(false);
    }
  };
  
  return (
    <Box sx={{ width: '100%', p: 3 }}>
      <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
        <Receipt sx={{ mr: 1, verticalAlign: 'middle' }} />
        Dashboard de Facturación
      </Typography>
      
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      
      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={3}>
          <Card sx={{ backgroundColor: '#e3f2fd' }}>
            <CardContent>
              <Typography variant="body2" color="text.secondary">Total Documentos</Typography>
              <Typography variant="h4" color="primary">{loading ? '...' : stats.totalDocumentos}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={3}>
          <Card sx={{ backgroundColor: '#e8f5e9' }}>
            <CardContent>
              <Typography variant="body2" color="text.secondary">Emitidos</Typography>
              <Typography variant="h4" color="success.main">{loading ? '...' : stats.emitidos}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={3}>
          <Card sx={{ backgroundColor: '#ffebee' }}>
            <CardContent>
              <Typography variant="body2" color="text.secondary">Anulados</Typography>
              <Typography variant="h4" color="error.main">{loading ? '...' : stats.anulados}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={3}>
          <Card sx={{ backgroundColor: '#fff3e0' }}>
            <CardContent>
              <Typography variant="body2" color="text.secondary">Total Monto</Typography>
              <Typography variant="h4" color="warning.main">${loading ? '...' : stats.totalMonto.toFixed(2)}</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      
      {/* Documents Table */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Últimos Documentos Emitidos
          </Typography>

          <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
            <Grid item xs={12} sm={4} md={3}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="Fecha de emisión"
                value={fechaEmisionFilter}
                onChange={(event) => {
                  setFechaEmisionFilter(event.target.value);
                  setPage(0);
                }}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={5} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel id="tipo-dte-filter-label">Tipo de documento</InputLabel>
                <Select
                  labelId="tipo-dte-filter-label"
                  value={tipoDteFilter}
                  label="Tipo de documento"
                  onChange={(event) => {
                    setTipoDteFilter(event.target.value);
                    setPage(0);
                  }}
                >
                  <MenuItem value="">Todos</MenuItem>
                  {Object.entries(DTE_TYPES).map(([code, name]) => (
                    <MenuItem key={code} value={code}>{code} - {name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={3} md={2}>
              <Button
                fullWidth
                variant="outlined"
                disabled={!fechaEmisionFilter && !tipoDteFilter}
                onClick={() => {
                  setFechaEmisionFilter('');
                  setTipoDteFilter('');
                  setPage(0);
                }}
              >
                Limpiar filtros
              </Button>
            </Grid>
          </Grid>
          
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : documents.length === 0 ? (
            <Alert severity="info">
              {fechaEmisionFilter || tipoDteFilter
                ? 'No se encontraron documentos con los filtros seleccionados.'
                : 'No hay documentos emitidos aún. Diríjase a "Nuevo DTE" o "Venta Rápida" para crear su primer documento.'}
            </Alert>
          ) : (
            <Box>
              <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                    <TableCell><strong>Tipo</strong></TableCell>
                    <TableCell><strong>N° Control</strong></TableCell>
                    <TableCell><strong>Cliente</strong></TableCell>
                    <TableCell align="right"><strong>Monto</strong></TableCell>
                    <TableCell><strong>Fecha</strong></TableCell>
                    <TableCell><strong>Estado</strong></TableCell>
                    <TableCell align="center"><strong>Acciones</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {documents.map((doc) => (
                    <React.Fragment key={doc.id || doc.cod_generacion}>
                      <TableRow hover>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Description fontSize="small" />
                            <Typography variant="body2">
                              {DTE_TYPES[doc.tipo_dte] || doc.tipo_dte}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                            {doc.numero_control || doc.cod_generacion?.substring(0, 8)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                            {doc.receptor_name || 'Consumidor Final'}
                          </Typography>
                          {doc.receptor_nit && (
                            <Typography variant="caption" color="text.secondary">
                              {doc.receptor_nit}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight="bold">
                            ${getDocumentAmount(doc).toFixed(2)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {doc.fecha_formatted}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={getStatusLabel(
                              doc.display_status === 'cancelled'
                                ? 'cancelled'
                                : (getMhEstado(doc) || (doc.display_status || doc.status))
                            )} 
                            color={getStatusColor(
                              doc.display_status === 'cancelled'
                                ? 'cancelled'
                                : (getMhEstado(doc) || (doc.display_status || doc.status))
                            )} 
                            size="small"
                          />
                        </TableCell>
                        <TableCell align="center">
                          <IconButton 
                            size="small" 
                            title="Ver"
                            onClick={() => handleViewDetails(doc)}
                          >
                            <Visibility fontSize="small" />
                          </IconButton>
                          {(getMhEstado(doc) === 'PROCESADO' || doc.display_status === 'PROCESADO') && (
                            <IconButton 
                              size="small" 
                              title="PDF"
                              color="error"
                              onClick={() => handleOpenPdfDialog(doc)}
                            >
                              <PictureAsPdf fontSize="small" />
                            </IconButton>
                          )}
                          {(getMhEstado(doc) === 'PROCESADO' || doc.display_status === 'PROCESADO') && (
                            <IconButton 
                              size="small" 
                              title="Enviar Correo"
                              color="primary"
                              onClick={() => handleOpenEmailDialog(doc)}
                            >
                              <Email fontSize="small" />
                            </IconButton>
                          )}
                          {(getMhEstado(doc) === 'PROCESADO' || doc.display_status === 'PROCESADO') && (
                            <IconButton 
                              size="small" 
                              title="Anular"
                              color="warning"
                              onClick={() => handleAnular(doc)}
                            >
                              <Cancel fontSize="small" />
                            </IconButton>
                          )}
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[10, 25, 50]}
              component="div"
              count={totalDocuments}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              labelRowsPerPage="Filas por página:"
              labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
            />
            </Box>
          )}
        </CardContent>
      </Card>
      
      {/* Details Dialog */}
      <Dialog 
        open={detailsDialogOpen} 
        onClose={handleCloseDetailsDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Detalles del Documento
        </DialogTitle>
        <DialogContent>
          {selectedDocument && (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">Tipo de Documento</Typography>
                <Typography variant="body1" fontWeight="bold">
                  {DTE_TYPES[selectedDocument.tipo_dte] || selectedDocument.tipo_dte}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">Número de Control</Typography>
                <Typography variant="body1" fontWeight="bold">
                  {selectedDocument.numero_control || selectedDocument.cod_generacion}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">Código de Generación</Typography>
                <Typography variant="body1" fontWeight="bold">
                  {selectedDocument.cod_generacion}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">Estado</Typography>
                <Chip 
                  label={getStatusLabel(selectedDocument.display_status || selectedDocument.status)} 
                  color={getStatusColor(selectedDocument.display_status || selectedDocument.status)} 
                  size="small"
                />
              </Grid>
              {selectedDocument.mh_estado && (
                <>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">Estado MH</Typography>
                    <Typography variant="body1">
                      {selectedDocument.mh_estado}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">Descripción MH</Typography>
                    <Typography variant="body1">
                      {selectedDocument.mh_descripcion || 'N/A'}
                    </Typography>
                  </Grid>
                  {selectedDocument.mh_sello_recepcion && (
                    <Grid item xs={12}>
                      <Typography variant="body2" color="text.secondary">Sello de Recepción</Typography>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                        {selectedDocument.mh_sello_recepcion}
                      </Typography>
                    </Grid>
                  )}
                  {selectedDocument.mh_observaciones && selectedDocument.mh_observaciones.length > 0 && (
                    <Grid item xs={12}>
                      <Typography variant="body2" color="text.secondary">Observaciones</Typography>
                      {Array.isArray(selectedDocument.mh_observaciones) ? (
                        selectedDocument.mh_observaciones.map((obs, idx) => (
                          <Typography key={idx} variant="body2" color="warning.main">
                            - {obs}
                          </Typography>
                        ))
                      ) : (
                        <Typography variant="body2" color="warning.main">
                          {selectedDocument.mh_observaciones}
                        </Typography>
                      )}
                    </Grid>
                  )}
                </>
              )}
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">Fecha de Emisión</Typography>
                <Typography variant="body1">
                  {selectedDocument.fecha_formatted}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">{getDocumentAmountLabel(selectedDocument)}</Typography>
                <Typography variant="body1" fontWeight="bold">
                  ${getDocumentAmount(selectedDocument).toFixed(2)}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary">Receptor</Typography>
                <Typography variant="body1">
                  {selectedDocument.receptor_name || 'Consumidor Final'}
                </Typography>
                {selectedDocument.receptor_nit && (
                  <Typography variant="body2" color="text.secondary">
                    NIT: {selectedDocument.receptor_nit}
                  </Typography>
                )}
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDetailsDialog}>Cerrar</Button>
        </DialogActions>
      </Dialog>
      
      {/* Anulacion Dialog */}
      <Dialog
        open={anulacionDialogOpen}
        onClose={handleCloseAnulacionDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Anular Documento Tributario Electrónico
        </DialogTitle>
        <DialogContent>
          {anulacionSuccess ? (
            <Alert severity="success" sx={{ mt: 2 }}>
              Documento anulado exitosamente. El estado se actualizará en breve.
            </Alert>
          ) : (
            <>
              {anulacionError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {anulacionError}
                </Alert>
              )}
              
              {/* Información del documento a anular */}
              <Alert severity="warning" sx={{ mb: 3 }}>
                <Typography variant="body2" fontWeight="bold">
                  Documento a anular:
                </Typography>
                <Typography variant="body2">
                  {DTE_TYPES[selectedDocument?.tipo_dte] || selectedDocument?.tipo_dte} - 
                  {selectedDocument?.numero_control || selectedDocument?.cod_generacion}
                </Typography>
                <Typography variant="body2">
                  Receptor: {selectedDocument?.receptor_name || 'Consumidor Final'}
                </Typography>
                <Typography variant="body2">
                  Monto: ${getDocumentAmount(selectedDocument).toFixed(2)}
                </Typography>
              </Alert>
              
              {/* Sección: Tipo de Anulación */}
              <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
                Tipo de Anulación
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <FormControl fullWidth required>
                    <InputLabel>Tipo de Anulación</InputLabel>
                    <Select
                      value={anulacionData.tipoAnulacion || ''}
                      onChange={handleAnulacionFieldChange('tipoAnulacion')}
                      label="Tipo de Anulación"
                    >
                      {ANULATION_TYPES.map((type) => (
                        <MenuItem key={type.code} value={type.code}>
                          {type.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                
                {/* Campo condicional: Código de reemplazo */}
                {anulacionData.tipoAnulacion === 1 && (
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Código de Generación de Reemplazo"
                      value={anulacionData.codigoGeneracionR}
                      onChange={handleAnulacionFieldChange('codigoGeneracionR')}
                      placeholder="Ingrese el código UUID del documento que reemplaza"
                      helperText="UUID formato: xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                    />
                  </Grid>
                )}
                
                {/* Campo condicional: Motivo de anulación para tipo 3 */}
                {anulacionData.tipoAnulacion === 3 && (
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Motivo de Anulación"
                      value={anulacionData.motivoAnulacion}
                      onChange={handleAnulacionFieldChange('motivoAnulacion')}
                      multiline
                      rows={2}
                      required
                      placeholder="Describa el motivo de la anulación"
                    />
                  </Grid>
                )}
              </Grid>
              
              {/* Sección: Datos del Responsable */}
              <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
                Datos del Responsable de la Anulación
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Nombre Completo"
                    value={anulacionData.nombreResponsable}
                    onChange={handleAnulacionFieldChange('nombreResponsable')}
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <FormControl fullWidth required>
                    <InputLabel>Tipo de Documento</InputLabel>
                    <Select
                      value={anulacionData.tipDocResponsable}
                      onChange={handleAnulacionFieldChange('tipDocResponsable')}
                      label="Tipo de Documento"
                    >
                      {ID_TYPES.map((type) => (
                        <MenuItem key={type.code} value={type.code}>
                          {type.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    fullWidth
                    label="Número de Documento"
                    value={anulacionData.numDocResponsable}
                    onChange={handleAnulacionFieldChange('numDocResponsable')}
                    required
                  />
                </Grid>
              </Grid>
              
              {/* Sección: Datos del Solicitante */}
              <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
                Datos del Solicitante
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Nombre Completo"
                    value={anulacionData.nombreSolicita}
                    onChange={handleAnulacionFieldChange('nombreSolicita')}
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <FormControl fullWidth required>
                    <InputLabel>Tipo de Documento</InputLabel>
                    <Select
                      value={anulacionData.tipDocSolicita}
                      onChange={handleAnulacionFieldChange('tipDocSolicita')}
                      label="Tipo de Documento"
                    >
                      {ID_TYPES.map((type) => (
                        <MenuItem key={type.code} value={type.code}>
                          {type.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    fullWidth
                    label="Número de Documento"
                    value={anulacionData.numDocSolicita}
                    onChange={handleAnulacionFieldChange('numDocSolicita')}
                    required
                  />
                </Grid>
              </Grid>
              
              {/* Datos del Emisor (solo lectura informativa) */}
              <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
                Datos del Emisor
              </Typography>
              <Alert severity="info" sx={{ mb: 2 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2">
                      <strong>Nombre:</strong> {emisorData?.legal_name || emisorData?.commercial_name || emisorData?.nombre || 'No disponible'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2">
                      <strong>NIT:</strong> {emisorData?.nit || 'No disponible'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2">
                      <strong>Correo:</strong> {emisorData?.bill_email || emisorData?.correo || 'No disponible'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2">
                      <strong>Teléfono:</strong> {emisorData?.bill_phone || emisorData?.telefono || 'No disponible'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2">
                      <strong>Establecimiento:</strong> {emisorData?.nomEstablecimiento || emisorData?.commercial_name || 'No disponible'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2">
                      <strong>Tipo Establec.:</strong> {emisorData?.tipoEstablecimiento || '01'} - {emisorData?.tipoEstablecimiento === '01' ? 'Matriz' : emisorData?.tipoEstablecimiento === '02' ? 'Sucursal' : 'Otro'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2">
                      <strong>Código Estable:</strong> {emisorData?.codEstable || '0001'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2">
                      <strong>Punto Venta:</strong> {emisorData?.codPuntoVenta || '0001'}
                    </Typography>
                  </Grid>
                </Grid>
              </Alert>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={handleCloseAnulacionDialog}
            disabled={anulacionLoading}
          >
            {anulacionSuccess ? 'Cerrar' : 'Cancelar'}
          </Button>
          {!anulacionSuccess && (
            <Button
              variant="contained"
              color="error"
              onClick={handleSubmitAnulacion}
              disabled={anulacionLoading}
              startIcon={anulacionLoading ? <CircularProgress size={20} /> : <Cancel />}
            >
              {anulacionLoading ? 'Anulando...' : 'Confirmar Anulación'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
      
      {/* PDF Dialog */}
      <Dialog
        open={pdfDialogOpen}
        onClose={handleClosePdfDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Generar PDF del Documento
          {pdfUrl && (
            <Chip 
              icon={<PictureAsPdf />} 
              label="PDF" 
              color="primary" 
              size="small" 
              sx={{ ml: 2 }}
            />
          )}
        </DialogTitle>
        <DialogContent dividers>
          {loadingInvoiceData ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 4 }}>
              <CircularProgress />
              <Typography sx={{ ml: 2 }}>Cargando datos del documento...</Typography>
            </Box>
          ) : (
            <>
              {pdfError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {pdfError}
                </Alert>
              )}
              
              {pdfUrl ? (
                <Box sx={{ mb: 2, p: 2, bgcolor: '#e3f2fd', borderRadius: 1 }}>
                  <Button 
                    variant="contained" 
                    startIcon={<PictureAsPdf />}
                    href={resolvePdfUrl(pdfUrl)}
                    target="_blank"
                    fullWidth
                  >
                    Abrir PDF
                  </Button>
                </Box>
              ) : (
                <Box sx={{ mb: 2 }}>
                  <Button 
                    variant="outlined" 
                    startIcon={<PictureAsPdf />}
                    onClick={handleGeneratePdf}
                    disabled={generatingPdf}
                    fullWidth
                  >
                    {generatingPdf ? 'Generando PDF...' : 'GENERAR PDF'}
                  </Button>
                </Box>
              )}
              
              {/* Mostrar datos del documento directamente */}
              {selectedDocument && (
                <>
                  <Typography variant="h6" gutterBottom>
                    Datos del Documento
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">Tipo</Typography>
                      <Typography variant="body1">
                        {DTE_TYPES[selectedDocument.tipo_dte] || selectedDocument.tipo_dte}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">Número de Control</Typography>
                      <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>
                        {selectedDocument.numero_control || selectedDocument.cod_generacion}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">Código Generación</Typography>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.7rem' }}>
                        {selectedDocument.cod_generacion || '-'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">Fecha</Typography>
                      <Typography variant="body1">
                        {selectedDocument.fecha_formatted || '-'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">{getDocumentAmountLabel(selectedDocument)}</Typography>
                      <Typography variant="body1" fontWeight="bold">
                        ${getDocumentAmount(selectedDocument).toFixed(2)}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">Estado MH</Typography>
                      <Typography variant="body1">
                        {selectedDocument.mh_estado || selectedDocument.display_status || '-'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="body2" color="text.secondary">Receptor</Typography>
                      <Typography variant="body1">
                        {selectedDocument.receptor_name || 'Consumidor Final'}
                      </Typography>
                      {selectedDocument.receptor_nit && (
                        <Typography variant="caption" color="text.secondary">
                          NIT: {selectedDocument.receptor_nit}
                        </Typography>
                      )}
                    </Grid>
                  </Grid>
                </>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePdfDialog}>Cerrar</Button>
        </DialogActions>
      </Dialog>
      
      {/* Email Dialog */}
      <Dialog
        open={emailDialogOpen}
        onClose={handleCloseEmailDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Enviar Documento por Correo Electrónico
        </DialogTitle>
        <DialogContent dividers>
          {emailSuccess ? (
            <Alert severity="success" sx={{ mt: 2 }}>
              Correo enviado exitosamente al destinatario.
            </Alert>
          ) : (
            <>
              {emailError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {emailError}
                </Alert>
              )}
              
              {/* Información del documento */}
              <Alert severity="info" sx={{ mb: 3 }}>
                <Typography variant="body2" fontWeight="bold">
                  Documento a enviar:
                </Typography>
                <Typography variant="body2">
                  {DTE_TYPES[selectedDocument?.tipo_dte] || selectedDocument?.tipo_dte} - 
                  {selectedDocument?.numero_control || selectedDocument?.cod_generacion}
                </Typography>
                <Typography variant="body2">
                  Receptor: {selectedDocument?.receptor_name || 'Consumidor Final'}
                </Typography>
                <Typography variant="body2">
                  Monto: ${getDocumentAmount(selectedDocument).toFixed(2)}
                </Typography>
              </Alert>
              
              {/* Campo de correo */}
              <TextField
                fullWidth
                label="Correo Electrónico"
                value={emailTo}
                onChange={(e) => setEmailTo(e.target.value)}
                placeholder="correo@ejemplo.com"
                type="email"
                required
                helperText="Ingrese el correo electrónico donde desea recibir el PDF"
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEmailDialog}>
            {emailSuccess ? 'Cerrar' : 'Cancelar'}
          </Button>
          {!emailSuccess && (
            <Button
              variant="contained"
              color="primary"
              onClick={handleSendEmail}
              disabled={emailLoading || !emailTo}
              startIcon={emailLoading ? <CircularProgress size={20} /> : <Email />}
            >
              {emailLoading ? 'Enviando...' : 'Enviar Correo'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BillingDashboard;
